import TagManager from "helpers/tag-manager";
import { debounce } from "utility/helpers";

const SearchPopup = {
	el: {
		root: document.querySelector(".search-popup"),
	},

	init() {
		if (this.el.root) {
			const el = {
				headerSearchButton: document.querySelector(".header__links--search"),
				searchPopup: document.querySelector<HTMLFormElement>(".search-popup"),
				popupForm: document.querySelector<HTMLFormElement>(
					".search-popup__form",
				),
				searchPopupInput: document.querySelector(
					".search-popup__input input",
				) as HTMLInputElement,
				suggestionsContainer: document.querySelector<HTMLDivElement>(
					".search-popup__suggestions-container",
				),
				suggestionsPopupResults: document.querySelector<HTMLDivElement>(
					".search-popup__suggestions",
				),
				suggestionsPopupData: document.querySelector<HTMLDivElement>(
					".search-popup-suggestion-data",
				),
				linkElement: document.querySelector<HTMLAnchorElement>(
					".search-popup__link",
				),
				lintTextElement: document.querySelector<HTMLSpanElement>(
					".search-popup-suggestion-data-total",
				),
			};

			const searchPagePath = el.linkElement
				.getAttribute("data-searchPageUrl")
				.replaceAll("/", "");
			const currentPath = window.location.pathname.replaceAll("/", "");

			if (searchPagePath === currentPath) {
				return;
			}

			if (el.searchPopupInput) {
				this.debounceInput(el);

				el.searchPopupInput.addEventListener("keyup", () => {
					//* wait to call the submit after the user has finished typing
					this.debounceInput(el);
				});
			}

			let searchPopupIsOpen = false;
			let isDesktop = false;

			if (window.innerWidth >= 1200) {
				isDesktop = true;
			}

			window.addEventListener("resize", () => {
				if (window.innerWidth >= 1200) {
					isDesktop = true;
				} else {
					isDesktop = false;
					searchPopupIsOpen = this.onClose(el);
				}
			});

			el.searchPopup.addEventListener("keydown", (e) => {
				const { key } = e;
				const searchPagePath =
					el.linkElement.getAttribute("data-searchPageUrl");

				if (key === "Escape") {
					searchPopupIsOpen = this.onClose(el);
				}

				if (key === "Enter") {
					e.preventDefault();
					if (el.searchPopupInput.value.length >= 2) {
						window.location.href = `${searchPagePath}?q=${el.searchPopupInput.value}`;
					}
				}
			});

			document.addEventListener("click", (event) => {
				const withinSearchPopupBoundaries = event
					.composedPath()
					.includes(el.searchPopup);
				const withinheaderSearchButtonBoundaries = event
					.composedPath()
					.includes(el.headerSearchButton);

				if (!isDesktop && withinheaderSearchButtonBoundaries) {
					window.location.href =
						el.linkElement.getAttribute("data-searchPageUrl");
				} else if (
					isDesktop &&
					searchPopupIsOpen &&
					(!withinSearchPopupBoundaries || withinheaderSearchButtonBoundaries)
				) {
					searchPopupIsOpen = this.onClose(el);
				} else if (
					isDesktop &&
					!searchPopupIsOpen &&
					withinheaderSearchButtonBoundaries
				) {
					searchPopupIsOpen = this.onOpen(el);
				}
			});

			this.submit(el);
		}
	},

	onOpen: (el) => {
		el.searchPopup.classList.add("search-popup--open");
		document.body.classList.add("h-disable-scroll");
		el.searchPopupInput.focus();

		return true;
	},

	onClose: (el) => {
		el.searchPopup.classList.remove("search-popup--open");
		document.body.classList.remove("h-disable-scroll");

		el.searchPopupInput.value = "";
		SearchPopup.closeSuggestions(el);

		return false;
	},

	togglePopup: (el) => {
		if (el.searchPopup.classList.contains("search-popup--visible")) {
			SearchPopup.onClose(el);
			return;
		}
		SearchPopup.onOpen(el);
	},

	sendData: async (el) => {
		el.suggestionsPopupResults.innerHTML = "";
		const action = el.popupForm.getAttribute("action");
		const lang = el.popupForm.getAttribute("data-lang");

		try {
			//* fetching search data
			const response = await fetch(
				`${action}?q=${el.searchPopupInput.value}&culture=${lang}`,
				{
					method: el.popupForm.getAttribute("method"),
				},
			);

			if (!response.ok) {
				console.error("Error fetching search data");
				return;
			}

			const result = await response.json();

			if (result.length < 1) {
				SearchPopup.closeSuggestions(el);
			} else if (result && result.length > 0) {
				SearchPopup.openSuggestions(el, result);
				TagManager.fireSearchEvent(el.searchPopupInput.value);
			}
		} catch (el) {
			SearchPopup.closeSuggestions(el);
		}
	},

	openSuggestions: (el, result) => {
		SearchPopup.renderResultsDataLink(el, result.length);
		SearchPopup.renderSuggestions(el, result);

		if (el.suggestionsContainer) {
			el.suggestionsContainer.classList.add(
				"search-popup__suggestions-container--open",
			);
		}
	},

	closeSuggestions: (el) => {
		el.suggestionsPopupResults.innerHTML = "";

		if (el.suggestionsContainer) {
			el.suggestionsContainer.classList.remove(
				"search-popup__suggestions-container--open",
			);
		}
	},

	renderResultsDataLink: (el, resultTotal) => {
		const linkText = `${el.linkElement.getAttribute("data-searchPageUrl")}?q=${el.searchPopupInput.value}`;

		el.linkElement.href = linkText;
		el.lintTextElement.innerHTML = resultTotal;
	},

	renderSuggestions: (el, result) => {
		const suggestions = result.slice(0, 4);

		suggestions.forEach((suggestion) => {
			el.suggestionsPopupResults.insertAdjacentHTML(
				"beforeend",
				SearchPopup.renderSuggestionItem(suggestion),
			);
		});
	},

	renderSuggestionItem: (suggestion) => {
		let url = suggestion.url;

		if (suggestion.docAlias === "employee") {
			url = url.split("/").slice(0, -2).join("/");
		}

		if (suggestion.docAlias === "faqItem") {
			const parts = url.split("/").filter((part) => part);
			const firstPart = parts[0] || null;
			const allExceptLast = parts.slice(0, -1);
			const resultWithoutLast = `/${allExceptLast.join("/")}`;

			url =
				firstPart === "spurt-og-svarad"
					? `${firstPart}#${suggestion.id}`
					: `${resultWithoutLast}#${suggestion.id}`;
		}

		return `<a href="${url}" class="search-popup__suggestions-item">${suggestion.title}</a>`;
	},

	debounceInput: (el) => {
		const debounceSubmit = debounce(() => SearchPopup.sendData(el));
		const debounceClose = debounce(() => SearchPopup.closeSuggestions(el));

		if (el.searchPopupInput.value.length >= 2) {
			debounceSubmit();
		} else {
			debounceClose();
		}
	},

	submit(el) {
		el.popupForm.addEventListener(
			"submit",
			(e: Event & { target: HTMLInputElement }) => {
				TagManager.fireSearchEvent(e.target.value);
			},
		);
	},
};

export default SearchPopup;
